import React from 'react'
import type { DashboardMetrics } from '../../hooks/useDashboard'
import { Card } from '../ui/Card'

interface OperationalHealthProps {
  health: DashboardMetrics['operational_health']
  loading?: boolean
}

export const OperationalHealth: React.FC<OperationalHealthProps> = ({ health, loading }) => {
  const formatTime = (minutes: number) => {
    if (minutes < 1) return `${Math.round(minutes * 60)}s`
    return `${Math.round(minutes)}m`
  }

  const getTimeColor = (minutes: number, threshold: number) => {
    if (minutes > threshold) return 'text-red-600'
    if (minutes > threshold * 0.8) return 'text-yellow-600'
    return 'text-green-600'
  }

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    )
  }

  const metrics = [
    {
      label: 'Avg Fulfillment Time',
      value: formatTime(health.avg_time_placed_to_delivered),
      threshold: 45,
      icon: '✅',
      description: 'Placed → delivered (completed orders)'
    },
    {
      label: 'P90 Fulfillment Time',
      value: formatTime(health.p90_placed_to_delivered),
      threshold: 60,
      icon: '📈',
      description: '90th percentile — slowest 10% of orders'
    },
    {
      label: 'Orders Past SLA',
      value: health.late_orders_count.toString(),
      threshold: 5,
      icon: '⚠️',
      description: 'Active orders open > 45 min',
      isCount: true
    },
    {
      label: 'Unpaid Deliveries',
      value: health.failed_payments_count.toString(),
      threshold: 3,
      icon: '💳',
      description: 'Delivered orders not yet marked paid',
      isCount: true
    }
  ]

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Operational Health</h3>
      <div className="space-y-3">
        {metrics.map((metric, index) => {
          const numValue = metric.isCount
            ? parseInt(metric.value)
            : parseFloat(metric.value);
          const colorClass = getTimeColor(numValue, metric.threshold);

          return (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-xl">{metric.icon}</div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{metric.label}</p>
                  <p className="text-xs text-gray-500">{metric.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-xl font-semibold ${colorClass}`}>{metric.value}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  )
}
